import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import type { InterpreterStore } from "@coltorapps/builder";

import { basicFormBuilder } from "@/components/form/builder/definition";
import { useDisableExpressionExecution } from "@/components/form/expression-execution-context";
import {
  defaultValueLoadingIdsAtom,
  expressionMasterDataCacheAtom,
  interpreterStoreAtom,
  runtimeApplicationAtom,
  selectedApplicantAtom,
} from "@/store";
import type { FormSchema } from "@/types/domain";
import { EntityKey } from "@/types/form-builder";
import apiCaller from "@/utils/api-caller";
import { toValidApplicantId } from "@/utils/applicant-id";
import { parseFormData } from "@/utils/parser";

import { useCodeHelper } from "./useCode/useCodeHelper";
import {
  extractReferencedMasterDataTableNames,
  needsServerExecution,
} from "./useCode/utils";
import { classifyExpressionDependencies } from "./useCode/expressionDependencies";

type RefEntry = {
  entityId: string;
  code: string;
  kind: "default-reference" | "expression-field";
  mode: "scoped" | "broad";
  depIds: string[];
  usesApplicantContext: boolean;
  masterDataTableKeys: string[];
  needsServer: boolean;
};

type EvaluationReason = "initial" | "dependency-change" | "context-change";

const EVAL_ERROR = Symbol("eval_error");
const EVAL_STALE = Symbol("eval_stale");
const EVALUATION_PENDING = Symbol("evaluation_pending");
const SERVER_EXECUTION_DEBOUNCE_MS = 700;
const ENABLE_REACTIVE_DEFAULT_DEBUG = false;

const logReactiveDefault = (
  entry: Pick<RefEntry, "entityId" | "code" | "needsServer" | "kind">,
  event: string,
  detail?: Record<string, unknown>,
) => {
  if (!ENABLE_REACTIVE_DEFAULT_DEBUG) {
    return;
  }
  console.debug("[useReactiveDefaultValues]", {
    entityId: entry.entityId,
    kind: entry.kind,
    needsServer: entry.needsServer,
    event,
    ...detail,
  });
};

const areValuesEqual = (a: unknown, b: unknown) => {
  if (Object.is(a, b)) {
    return true;
  }
  if (
    typeof a === "object" &&
    a !== null &&
    typeof b === "object" &&
    b !== null
  ) {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
};

const isEmptyExecutionValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (typeof value === "number" && Number.isNaN(value));

const hasUnresolvedDependencies = (
  entry: Pick<RefEntry, "depIds">,
  values: Record<string, unknown>,
) => entry.depIds.some((depId) => isEmptyExecutionValue(values[depId]));

function extractDefaultValueReference(
  attrs: Record<string, unknown>,
): string | undefined {
  const dv = attrs.defaultValue as
    | { isReference?: boolean; reference?: unknown }
    | undefined;
  if (
    dv?.isReference &&
    typeof dv.reference === "string" &&
    dv.reference.trim()
  ) {
    return dv.reference.trim();
  }

  const ds = attrs.datasourceType as
    | {
        type?: string;
        defaultValue?: { isReference?: boolean; reference?: unknown } | unknown;
      }
    | undefined;
  if (
    ds &&
    (ds.type === "static" || ds.type === "dynamic") &&
    ds.defaultValue &&
    typeof ds.defaultValue === "object"
  ) {
    const dsDV = ds.defaultValue as {
      isReference?: boolean;
      reference?: unknown;
    };
    if (
      dsDV.isReference &&
      typeof dsDV.reference === "string" &&
      dsDV.reference.trim()
    ) {
      return dsDV.reference.trim();
    }
  }

  return undefined;
}

function extractExpressionFieldCode(entity: {
  type?: string;
  attributes?: Record<string, unknown>;
}): string | undefined {
  if (entity.type !== EntityKey.expressionField) {
    return undefined;
  }

  const expression = entity.attributes?.expression;
  if (typeof expression !== "string" || !expression.trim()) {
    return undefined;
  }

  return expression.trim();
}

/**
 * Extract a static (non-reference) default value from entity attributes.
 * This covers defaults that are NOT expressions but literal values compiled
 * into the schema (e.g., compiled Date.now() result, static dropdown defaults
 * like "08:00").
 */
function extractStaticDefaultValue(
  attrs: Record<string, unknown>,
): unknown | undefined {
  // 1. Check attrs.defaultValue with compiled value
  const dv = attrs.defaultValue as
    | { isReference?: boolean; value?: unknown }
    | number
    | string
    | undefined;

  if (typeof dv === "number" || typeof dv === "string") {
    return dv;
  }

  if (dv && typeof dv === "object" && "value" in dv && dv.value != null) {
    // Skip if it's a reference that will be handled by extractDefaultValueReference
    if ((dv as { isReference?: boolean }).isReference) {
      return undefined;
    }
    return dv.value;
  }

  // 2. Check datasourceType.defaultValue for dropdown static defaults
  const ds = attrs.datasourceType as
    | {
        type?: string;
        defaultValue?:
          | { isReference?: boolean; value?: unknown; reference?: unknown }
          | unknown;
      }
    | undefined;

  if (
    ds &&
    (ds.type === "static" || ds.type === "dynamic") &&
    ds.defaultValue &&
    typeof ds.defaultValue === "object"
  ) {
    const dsDV = ds.defaultValue as {
      isReference?: boolean;
      value?: unknown;
      reference?: unknown;
    };
    // Skip if it's a reference
    if (dsDV.isReference) {
      return undefined;
    }
    if (dsDV.value != null) {
      return dsDV.value;
    }
  }

  return undefined;
}

function mergeCompiledDefaultsIntoValues(
  values: Record<string, unknown>,
  entities: FormSchema["entities"],
) {
  const mergedValues = { ...values };

  Object.entries(entities).forEach(([entityId, entity]) => {
    if (mergedValues[entityId] !== undefined) {
      return;
    }

    const attrs = (entity?.attributes ?? {}) as Record<string, unknown>;
    const staticValue = extractStaticDefaultValue(attrs);

    if (staticValue !== undefined) {
      mergedValues[entityId] = staticValue;
    }
  });

  return mergedValues;
}

export function useReactiveDefaultValues(
  interpreterStore: InterpreterStore<typeof basicFormBuilder> | null,
  schema: FormSchema,
): void {
  const disableExpressionExecution = useDisableExpressionExecution();
  const expressionMasterDataCache = useAtomValue(expressionMasterDataCacheAtom);
  const setLoadingIds = useSetAtom(defaultValueLoadingIdsAtom);
  const stableEmptyFormDataRef = useRef<Record<string, unknown>>({});

  const jotaiStore = useStore();
  const selectedApplicant = useAtomValue(selectedApplicantAtom);
  // making sure that intepreterStore is clean
  useLayoutEffect(() => {
    if (interpreterStore) {
      jotaiStore.set(interpreterStoreAtom, interpreterStore);
    }
  }, [interpreterStore, jotaiStore]);

  const runtimeApplication = useAtomValue(runtimeApplicationAtom);
  const disableEvaluation =
    disableExpressionExecution ||
    runtimeApplication?.reviewStatus === "Approved" ||
    runtimeApplication?.reviewStatus === "Rejected";
  const { compileReference, executeCode } = useCodeHelper({
    formSchema: schema,
    formData: stableEmptyFormDataRef.current,
    application: runtimeApplication,
  });
  const globalContextIdentity = useMemo(() => {
    try {
      return JSON.stringify({ p: selectedApplicant, a: runtimeApplication });
    } catch {
      return "";
    }
  }, [runtimeApplication, selectedApplicant]);

  const compileReferenceRef = useRef(compileReference);
  const executeCodeRef = useRef(executeCode);
  const hasStartedInitialExecutionRef = useRef<Set<string>>(new Set());
  const hasCompletedInitialExecutionRef = useRef<Set<string>>(new Set());
  const lastGlobalContextIdentityRef = useRef<string | null>(null);
  // Also keep a stable ref to the live refs map so the context-change effect
  // can re-evaluate entries without being inside the main setup effect.
  const refsRef = useRef<Map<string, RefEntry>>(new Map());
  const triggerEntryEvaluationRef = useRef<
    ((entry: RefEntry, reason: EvaluationReason) => void) | null
  >(null);
  useEffect(() => {
    compileReferenceRef.current = compileReference;
    executeCodeRef.current = executeCode;
  }, [compileReference, executeCode]);

  useEffect(() => {
    hasStartedInitialExecutionRef.current = new Set();
    hasCompletedInitialExecutionRef.current = new Set();
    lastGlobalContextIdentityRef.current = null;
    refsRef.current = new Map();
    triggerEntryEvaluationRef.current = null;
  }, [interpreterStore]);

  const evaluateLocal = useMemo(
    () =>
      (entry: RefEntry, currentValue: unknown): unknown => {
        if (
          entry.masterDataTableKeys.some((tableKey) => {
            const status = expressionMasterDataCache[tableKey]?.status;
            return !status || status === "idle" || status === "loading";
          })
        ) {
          return EVALUATION_PENDING;
        }

        try {
          if (entry.kind === "expression-field") {
            const executable = executeCodeRef.current(entry.code);
            return typeof executable === "function"
              ? executable(currentValue)
              : executable;
          }

          return compileReferenceRef.current(entry.code);
        } catch (error) {
          console.warn(
            entry.kind === "expression-field"
              ? `Failed to evaluate expression field "${entry.code}" for entity ${entry.entityId}`
              : `Failed to evaluate default-value reference "${entry.code}" for entity ${entry.entityId}`,
            error,
          );
          return EVAL_ERROR;
        }
      },
    [expressionMasterDataCache],
  );

  useEffect(() => {
    if (!interpreterStore) {
      refsRef.current = new Map();
      triggerEntryEvaluationRef.current = null;
      return;
    }
    if (disableEvaluation) {
      refsRef.current = new Map();
      triggerEntryEvaluationRef.current = null;
      return;
    }

    let isCurrent = true;
    const internalUpdatedIds = new Set<string>();

    const refs = new Map<string, RefEntry>();
    const depIndex = new Map<string, Set<string>>();
    const broadDependents = new Set<string>();

    Object.entries(schema.entities).forEach(([entityId, entity]) => {
      const attrs = (entity?.attributes ?? {}) as Record<string, unknown>;
      const defaultReferenceCode = extractDefaultValueReference(attrs);
      const expressionFieldCode = extractExpressionFieldCode(
        entity as {
          type?: string;
          attributes?: Record<string, unknown>;
        },
      );
      const code = defaultReferenceCode ?? expressionFieldCode;
      if (!code) {
        return;
      }

      const { mode, dependencyIds, usesApplicantContext } =
        classifyExpressionDependencies(code, schema.entities);
      refs.set(entityId, {
        entityId,
        code,
        kind: defaultReferenceCode ? "default-reference" : "expression-field",
        mode,
        depIds: dependencyIds,
        usesApplicantContext,
        masterDataTableKeys: extractReferencedMasterDataTableNames(code),
        needsServer: needsServerExecution(code),
      });

      if (mode === "broad") {
        broadDependents.add(entityId);
        return;
      }

      dependencyIds.forEach((depId) => {
        let set = depIndex.get(depId);
        if (!set) {
          set = new Set<string>();
          depIndex.set(depId, set);
        }
        set.add(entityId);
      });
    });

    if (refs.size === 0) {
      refsRef.current = new Map();
      triggerEntryEvaluationRef.current = null;
      return;
    }

    // Expose the current refs map so the context-change effect below can
    // re-evaluate entries without triggering a full effect teardown/setup.
    refsRef.current = refs;

    const serverInFlight = new Set<string>();
    const serverPendingRefetch = new Set<string>();
    const serverPendingRefetchReason = new Map<string, EvaluationReason>();
    const debouncedServerTimeouts = new Map<
      string,
      ReturnType<typeof setTimeout>
    >();

    const scheduleServerSync = (entry: RefEntry, reason: EvaluationReason) => {
      if (reason === "initial") {
        syncReferenceValue(entry, reason);
        return;
      }

      const existingTimeout = debouncedServerTimeouts.get(entry.entityId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      logReactiveDefault(entry, "debounce-server", {
        reason,
        delay: SERVER_EXECUTION_DEBOUNCE_MS,
      });

      const timeoutId = setTimeout(() => {
        debouncedServerTimeouts.delete(entry.entityId);
        if (!isCurrent) return;
        syncReferenceValue(entry, reason);
      }, SERVER_EXECUTION_DEBOUNCE_MS);

      debouncedServerTimeouts.set(entry.entityId, timeoutId);
    };

    const evaluateServer = async (entry: RefEntry): Promise<unknown> => {
      if (serverInFlight.has(entry.entityId)) {
        serverPendingRefetch.add(entry.entityId);
        serverPendingRefetchReason.set(entry.entityId, "dependency-change");
        logReactiveDefault(entry, "queue-refetch", {
          reason: "dependency-change",
        });
        return EVAL_ERROR;
      }
      serverInFlight.add(entry.entityId);
      setLoadingIds((prev) => new Set(prev).add(entry.entityId));
      try {
        const runtimeFormData =
          (runtimeApplication?.formInstance?.data as Record<string, unknown>) ??
          {};
        const liveValues = (interpreterStore.getEntitiesValues() ??
          {}) as Record<string, unknown>;
        const mergedValues = mergeCompiledDefaultsIntoValues(
          {
            ...runtimeFormData,
            ...liveValues,
          },
          schema.entities,
        );
        const formData = parseFormData(mergedValues, schema).data;
        const currentApplicant = jotaiStore.get(selectedApplicantAtom);
        const response = await apiCaller.post("/execution/fetch", {
          function: entry.code,
          formData,
          serialNumber: runtimeApplication?.serialNumber,
          applicantId:
            toValidApplicantId(currentApplicant?.id) ??
            toValidApplicantId(runtimeApplication?.applicantId) ??
            toValidApplicantId(runtimeApplication?.submittedBy),
        });
        logReactiveDefault(entry, "server-result", {
          nextValue: response.data,
        });
        if (serverPendingRefetch.has(entry.entityId)) {
          return EVAL_STALE;
        }
        if (entry.kind === "expression-field") {
          return typeof response.data === "object"
            ? JSON.stringify(response.data)
            : String(response.data);
        }

        return typeof response.data === "object" &&
          !Array.isArray(response.data)
          ? JSON.stringify(response.data)
          : response.data;
      } catch (error) {
        console.warn(
          entry.kind === "expression-field"
            ? `Failed to evaluate expression field via server for entity ${entry.entityId}`
            : `Failed to evaluate default-value reference via server for entity ${entry.entityId}`,
          error,
        );
        return EVAL_ERROR;
      } finally {
        serverInFlight.delete(entry.entityId);
        if (isCurrent) {
          if (serverPendingRefetch.has(entry.entityId)) {
            serverPendingRefetch.delete(entry.entityId);
            const nextReason =
              serverPendingRefetchReason.get(entry.entityId) ??
              "dependency-change";
            serverPendingRefetchReason.delete(entry.entityId);
            scheduleServerSync(entry, nextReason);
          } else {
            setLoadingIds((prev) => {
              const next = new Set(prev);
              next.delete(entry.entityId);
              return next;
            });
          }
        }
      }
    };

    const syncReferenceValue = (entry: RefEntry, reason: EvaluationReason) => {
      const entityValues = interpreterStore.getEntitiesValues() as Record<
        string,
        unknown
      >;
      const current = entityValues[entry.entityId];
      if (
        reason === "initial" &&
        (hasStartedInitialExecutionRef.current.has(entry.entityId) ||
          hasCompletedInitialExecutionRef.current.has(entry.entityId) ||
          !isEmptyExecutionValue(current))
      ) {
        logReactiveDefault(entry, "skip-initial", {
          reason,
          currentValue: current,
          hasStartedInitialExecution: hasStartedInitialExecutionRef.current.has(
            entry.entityId,
          ),
          hasCompletedInitialExecution:
            hasCompletedInitialExecutionRef.current.has(entry.entityId),
        });
        return;
      }

      if (hasUnresolvedDependencies(entry, entityValues)) {
        logReactiveDefault(entry, "skip-unresolved-dependencies", {
          reason,
          dependencyIds: entry.depIds,
        });
        return;
      }

      logReactiveDefault(entry, "execute", {
        reason,
        currentValue: current,
      });

      if (entry.needsServer) {
        if (serverInFlight.has(entry.entityId)) {
          if (reason === "initial") {
            logReactiveDefault(entry, "skip-server-inflight", { reason });
            return;
          }
          serverPendingRefetch.add(entry.entityId);
          serverPendingRefetchReason.set(entry.entityId, reason);
          logReactiveDefault(entry, "queue-refetch", { reason });
          return;
        }
        if (reason === "initial") {
          hasStartedInitialExecutionRef.current.add(entry.entityId);
        }
        evaluateServer(entry).then((evaluated) => {
          if (evaluated === EVAL_ERROR || evaluated === EVAL_STALE) return;
          if (reason === "initial") {
            hasCompletedInitialExecutionRef.current.add(entry.entityId);
          }
          if (evaluated === undefined) {
            logReactiveDefault(entry, "skip-undefined-result", { reason });
            return;
          }
          const nextValue = evaluated;
          const currentValue =
            interpreterStore.getEntitiesValues()[entry.entityId];
          if (!areValuesEqual(nextValue, currentValue)) {
            logReactiveDefault(entry, "commit-server-result", {
              reason,
              currentValue,
              nextValue,
            });
            internalUpdatedIds.add(entry.entityId);
            interpreterStore.setEntityValue(entry.entityId, nextValue as never);
          }
        });
        return;
      }
      const evaluated = evaluateLocal(entry, current);
      if (evaluated === EVAL_ERROR || evaluated === EVALUATION_PENDING) {
        logReactiveDefault(entry, "skip-local-pending-or-error", {
          reason,
          evaluated: evaluated === EVALUATION_PENDING ? "pending" : "error",
        });
        return;
      }
      if (reason === "initial") {
        hasStartedInitialExecutionRef.current.add(entry.entityId);
        hasCompletedInitialExecutionRef.current.add(entry.entityId);
      }
      // Handle async function expressions that return a Promise
      if (evaluated instanceof Promise) {
        evaluated
          .then((resolved) => {
            if (!isCurrent) return;
            if (resolved === undefined) {
              logReactiveDefault(entry, "skip-undefined-result", { reason });
              return;
            }
            const currentValue =
              interpreterStore.getEntitiesValues()[entry.entityId];
            if (!areValuesEqual(resolved, currentValue)) {
              logReactiveDefault(entry, "commit-local-result", {
                reason,
                currentValue,
                nextValue: resolved,
              });
              internalUpdatedIds.add(entry.entityId);
              interpreterStore.setEntityValue(
                entry.entityId,
                resolved as never,
              );
            }
          })
          .catch((err) => {
            console.warn(
              entry.kind === "expression-field"
                ? `Failed to evaluate async expression field for entity ${entry.entityId}`
                : `Failed to evaluate async default-value reference for entity ${entry.entityId}`,
              err,
            );
          });
        return;
      }
      if (evaluated === undefined) {
        logReactiveDefault(entry, "skip-undefined-result", { reason });
        return;
      }
      const currentValue = interpreterStore.getEntitiesValues()[entry.entityId];
      if (areValuesEqual(evaluated, currentValue)) {
        logReactiveDefault(entry, "skip-same-value", {
          reason,
          currentValue,
        });
        return;
      }
      logReactiveDefault(entry, "commit-local-result", {
        reason,
        currentValue,
        nextValue: evaluated,
      });
      internalUpdatedIds.add(entry.entityId);
      interpreterStore.setEntityValue(entry.entityId, evaluated as never);
    };

    triggerEntryEvaluationRef.current = (entry, reason) => {
      if (entry.needsServer) {
        scheduleServerSync(entry, reason);
        return;
      }
      syncReferenceValue(entry, reason);
    };

    refs.forEach((entry) => {
      const current = interpreterStore.getEntitiesValues()[entry.entityId];
      if (!isEmptyExecutionValue(current)) {
        hasStartedInitialExecutionRef.current.add(entry.entityId);
        hasCompletedInitialExecutionRef.current.add(entry.entityId);
      }
    });

    const lastObservedEntityValues = new Map<string, unknown>(
      Object.entries(interpreterStore.getEntitiesValues() ?? {}),
    );

    const unsubscribe = interpreterStore.subscribe(
      (_data: unknown, events: any[]) => {
        const valueUpdatedEvents = events
          .filter((event) => event?.name === "EntityValueUpdated")
          .map((event) => ({
            entityId: event?.payload?.entityId,
            value: event?.payload?.value,
          }))
          .filter(
            (
              event,
            ): event is {
              entityId: string;
              value: unknown;
            } => typeof event.entityId === "string",
          );

        if (valueUpdatedEvents.length === 0) {
          return;
        }
        const latestUpdatedValues = new Map<string, unknown>();
        valueUpdatedEvents.forEach(({ entityId, value }) => {
          const previousValue = latestUpdatedValues.has(entityId)
            ? latestUpdatedValues.get(entityId)
            : lastObservedEntityValues.get(entityId);
          if (areValuesEqual(previousValue, value)) {
            return;
          }
          latestUpdatedValues.set(entityId, value);
        });

        if (latestUpdatedValues.size === 0) {
          return;
        }
        latestUpdatedValues.forEach((value, entityId) => {
          lastObservedEntityValues.set(entityId, value);
        });

        const updatedIds = Array.from(latestUpdatedValues.keys());

        const internallyUpdatedIds = new Set<string>();
        updatedIds.forEach((id) => {
          if (internalUpdatedIds.has(id)) {
            internalUpdatedIds.delete(id);
            internallyUpdatedIds.add(id);
          }
        });

        if (updatedIds.length === 0) {
          return;
        }

        const dependents = new Set<string>();
        updatedIds.forEach((id) => {
          const set = depIndex.get(id);
          if (set) {
            set.forEach((d) => dependents.add(d));
          }
        });
        const shouldNotifyBroadDependents = updatedIds.some((id) => {
          if (!internallyUpdatedIds.has(id)) {
            return true;
          }

          return refs.get(id)?.kind !== "expression-field";
        });

        if (shouldNotifyBroadDependents) {
          broadDependents.forEach((d) => dependents.add(d));
        }

        updatedIds.forEach((id) => dependents.delete(id));

        if (dependents.size === 0) {
          return;
        }

        // Defer re-evaluation until the current mutation tick completes so
        // getFormField(...) reads the latest source field value.
        setTimeout(() => {
          if (!isCurrent) return;
          dependents.forEach((entityId) => {
            const entry = refs.get(entityId);
            if (entry) {
              logReactiveDefault(entry, "dependency-change", {
                updatedEntityIds: updatedIds,
              });
              if (entry.needsServer) {
                scheduleServerSync(entry, "dependency-change");
                return;
              }
              syncReferenceValue(entry, "dependency-change");
            }
          });
        }, 0);
      },
    );

    // Defer initial evaluation so caller hydration effects can populate
    // interpreter values before we decide whether a field is "empty".
    const initialRunTimeout = setTimeout(() => {
      if (!isCurrent) return;

      refs.forEach((entry) => syncReferenceValue(entry, "initial"));

      // Also apply static (non-reference) default values that are not
      // handled by the ref-based system above. These are literal values
      // compiled into the schema (e.g., dropdown "08:00", compiled Date.now()).
      Object.entries(schema.entities).forEach(([entityId, entity]) => {
        // Skip entities already handled by the reference system
        if (refs.has(entityId)) {
          return;
        }
        const attrs = (entity?.attributes ?? {}) as Record<string, unknown>;
        const staticValue = extractStaticDefaultValue(attrs);
        if (staticValue === undefined) {
          return;
        }
        const current = interpreterStore.getEntitiesValues()[entityId];
        if (!isEmptyExecutionValue(current)) {
          return;
        }
        internalUpdatedIds.add(entityId);
        interpreterStore.setEntityValue(entityId, staticValue as never);
      });
    }, 0);

    return () => {
      isCurrent = false;
      refsRef.current = new Map();
      triggerEntryEvaluationRef.current = null;
      clearTimeout(initialRunTimeout);
      debouncedServerTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      debouncedServerTimeouts.clear();
      // Clear loading IDs for any fetches that were in-flight from this effect
      if (serverInFlight.size > 0) {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          serverInFlight.forEach((id) => next.delete(id));
          return next;
        });
      }
      unsubscribe();
    };
  }, [
    disableEvaluation,
    evaluateLocal,
    interpreterStore,
    runtimeApplication,
    schema,
    setLoadingIds,
  ]);

  // Re-evaluate reactive entries when applicant/application context actually
  // changes. These entries may also depend on form fields; the context-based
  // rerun is an additional trigger, not a replacement for dependency updates.
  useEffect(() => {
    if (!interpreterStore) return;
    if (disableEvaluation) return;

    if (lastGlobalContextIdentityRef.current === null) {
      lastGlobalContextIdentityRef.current = globalContextIdentity;
      return;
    }
    if (lastGlobalContextIdentityRef.current === globalContextIdentity) {
      return;
    }
    lastGlobalContextIdentityRef.current = globalContextIdentity;

    refsRef.current.forEach((entry) => {
      if (!entry.usesApplicantContext) {
        return;
      }
      logReactiveDefault(entry, "context-change", {
        contextIdentity: globalContextIdentity,
      });
      triggerEntryEvaluationRef.current?.(entry, "context-change");
    });
  }, [disableEvaluation, globalContextIdentity, interpreterStore]);
}
